import Cookies from 'js-cookie';
import * as Logger from '../../common/tools/logger';
import {
  BRAND_DOMAIN,
  OPT_IN_SERVICE_ADITION,
  COOKIE_IDENTIFIER_UI_CONSENT
} from './config';
import { UiPermissionData } from './interface';
import { PermissionFeatureTypes } from '@uim-tam/tcf-api/types/index';

// Reflects, if we already triggered the optin request for ADITION
let aditionOptInTriggered = false;

/**
 * Request the ADITION optin service (once a lifecycle)
 */
const aditionOptIn = (): void => {
  if (aditionOptInTriggered) return;

  new Image(1, 1).src = OPT_IN_SERVICE_ADITION;
  aditionOptInTriggered = true;
};

/**
 * Get the required permission data for the `ui_cid` cookie
 */
const getUiPermissionData = (): UiPermissionData => {

  // Stores the permission data, will be returned after processing
  const uiPermissionData: UiPermissionData = {};

  // Permission Features
  ([
    'brainTracking',
    'uimservTracking',
    'agofTracking',
    'tgp',
    'oewaTracking',
    'googleAnalyticsTracking',
    'editorialPersonalization',
    'aditionAds',
    'siteSpectTesting',
    'fullConsent',
    'googleAc',
    'amazonAds',
    'specialGeoFeature'
  ] as PermissionFeatureTypes[]).forEach((command: PermissionFeatureTypes) => {
    window.__tcfapi('getPermission', 2, (permission: boolean) => {
      if (permission) {
        if (!Object.prototype.hasOwnProperty.call(uiPermissionData, 'permissionFeature')) {
          uiPermissionData.permissionFeature = [];
        }
        uiPermissionData.permissionFeature.push(command);
      }
    }, command);
  });

  // If `fullConsent` is given, stop processing, remove all other `permissionFeature`'s and return the `uiPermissionData`
  if (uiPermissionData.permissionFeature && uiPermissionData.permissionFeature.indexOf('fullConsent') !== -1) {
    uiPermissionData.permissionFeature = ['fullConsent'];
    return uiPermissionData;
  }

  // Publisher Permissions
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach((pId: number) => {
    window.__tcfapi('getPermission', 2, (permission: boolean) => {
      if (permission) {
        if (!Object.prototype.hasOwnProperty.call(uiPermissionData, 'publisher')) {
          uiPermissionData.publisher = [];
        }
        uiPermissionData.publisher.push(pId);
        aditionOptIn();
      }
    }, 'publisher', pId);
  });

  // Purpose Permissions
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach((pId: number) => {
    window.__tcfapi('getPermission', 2, (permission: boolean) => {
      if (permission) {
        if (!Object.prototype.hasOwnProperty.call(uiPermissionData, 'purpose')) {
          uiPermissionData.purpose = [];
        }
        uiPermissionData.purpose.push(pId);
        aditionOptIn();
      }
    }, 'purpose', pId);
  });

  // Custom Publisher Purpose Permissions
  [1, 2, 3].forEach((pId: number) => {
    window.__tcfapi('getPermission', 2, (permission: boolean) => {
      if (permission) {
        if (!Object.prototype.hasOwnProperty.call(uiPermissionData, 'publisherCustomPurpose')) {
          uiPermissionData.publisherCustomPurpose = [];
        }
        uiPermissionData.publisherCustomPurpose.push(pId);
      }
    }, 'publisherCustomPurpose', pId);
  });

  // Special Feature Permissions
  [1, 2].forEach((pId: number) => {
    window.__tcfapi('getPermission', 2, (permission: boolean) => {
      if (permission) {
        if (!Object.prototype.hasOwnProperty.call(uiPermissionData, 'specialFeature')) {
          uiPermissionData.specialFeature = [];
        }
        uiPermissionData.specialFeature.push(pId);
      }
    }, 'special', pId);
  });

  return uiPermissionData;
}

/**
 * Function to store the ui consent(s) into its cookie
 */
const setUiConsentCookie = (): void => {

  let uiConsentCookieData: string;
  try {
    // Get the ui permission data and stringify it
    uiConsentCookieData = JSON.stringify(getUiPermissionData());
  } catch (error) {
    // Privacy by default
    uiConsentCookieData = '{}';
    Logger.error(`Compat`, `setUiConsentCookie`, `Could not parse 'uiConsentCookieData', applying privacy by default.`, error);
  }

  // Important to write the cookie `withConverter`, otherwise the cookie value would be encoded
  Cookies.set(COOKIE_IDENTIFIER_UI_CONSENT, uiConsentCookieData, {
    domain: BRAND_DOMAIN,
    expires: 390,
    secure: window.location.protocol === 'https:' ? true : false,
  });
}

/**
 * Updates the `uiconsent` cookie with the permissions we've got from the user
 */
export const updateUiConsentCookie = (): void => {
  if (typeof window.__tcfapi !== 'function') {
    Logger.error(`Compat`, `updateUiConsentCookie`, `TCF API not initialized, could not update the value of the 'uiconsent' cookie.`);
    return;
  }
  setUiConsentCookie();
}
